import {MultiSelectionBox} from 'notebook/tables/constants/TableSelectionConstants';
import {
  computeAnchorSelectedEvaluatedCells,
  computeIsCellSelectionEditingLocked,
  computeIsTableLocked,
  computeLockedWarningMessage,
  computeSelectedEvaluatedCells,
} from 'notebook/tables/store/logic/TableLockingLogic';
import {createTableModelSelector} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {TableEditableStore, TableEditableVolatileProps} from 'notebook/tables/store/TableStore';
import {
  getLockedCellWarningMessage,
  getLockedTableWarningMessage,
  LockedCellWarningType,
  LockedTableWarningType,
} from 'notebook/tables/util/LockedTableWarningMessages';

import {TableSelectionModel} from './TableSelectionSelectors';

/**
 * Model containing properties related to table locking and permissions about the editability of the table.
 */
export interface TableLockingModel {
  /**
   * Whether the table is in a locked state.
   * A table is locked if the locked prop is true OR if it has placeholder rows.
   */
  isTableLocked: boolean;

  /**
   * Whether editing is locked for the current cell selection (full selection, not anchor-only).
   */
  isCellSelectionLocked: boolean;

  /**
   * Whether editing is locked for the current cell selection.
   * Same as isCellSelectionLocked; used by isLockedWithEditWarn which may show a notification.
   */
  isLockedWithEditWarn: boolean;

  /**
   * Warning message when editing is locked for the current selection, or null.
   */
  lockedWarningMessage: string | null;

  /**
   * Whether editing is locked when considering only the anchor cell (and its merged range).
   */
  isAnchorCellSelectionLocked: boolean;

  /**
   * Whether editing is locked for undo/redo (allows actions involving locked cells).
   */
  isCellSelectionLockedForUndoRedo: boolean;

  /**
   * Warning message to show when the table locked prop is true (e.g. table context menu).
   */
  editWhileTableLockedWarningMessage: string | null;

  /**
   * Warning message when the anchor cell selection is locked, using the expanded selection for cell warnings.
   */
  lockedWarningMessageIfAnchorLocked: string | null;

  /**
   * Warning message for undo/redo locking checks.
   */
  lockedWarningMessageForUndoRedo: string | null;
}

function resolveEditWhileTableLockedWarning(
  editWhileTableLockedWarning: string | null | undefined
): string | null {
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (editWhileTableLockedWarning === null) {
    return null;
  }
  return editWhileTableLockedWarning ?? getLockedTableWarningMessage(LockedTableWarningType.GENERIC);
}

function resolveGetEditWhileCellLockedWarning(
  getEditWhileCellLockedWarning: ((selection: MultiSelectionBox) => string) | null | undefined
): ((selection: MultiSelectionBox) => string) | null {
  // eslint-disable-next-line benchlint/no-eqeqeq-null
  if (getEditWhileCellLockedWarning === null) {
    return null;
  }
  return getEditWhileCellLockedWarning ?? (() => getLockedCellWarningMessage(LockedCellWarningType.GENERIC));
}

/**
 * Creates a memoized selector that computes the TableLockingModel from the store.
 */
export function createTableLockingSelector(
  volatilePropsSelector: (store: TableEditableStore) => TableEditableVolatileProps,
  tableSelectionSelector: (store: TableEditableStore) => TableSelectionModel
): (store: TableEditableStore) => TableLockingModel {
  return createTableModelSelector(
    [
      (store) => store.stableProps.locked ?? false,
      (store) => store.stableProps.hasPlaceholderRows ?? false,
      tableSelectionSelector,
      (store) => volatilePropsSelector(store).computedTable,
      (store) => volatilePropsSelector(store).table,
      (store) => store.stableProps.editWhileTableLockedWarning,
      (store) => store.stableProps.getEditWhileCellLockedWarning,
      (store) => store.state.multiStartIndex,
    ],
    (
      locked,
      hasPlaceholderRows,
      selection,
      computedTable,
      table,
      editWhileTableLockedWarningProp,
      getEditWhileCellLockedWarningProp,
      multiStartIndex
    ) => {
      const isTableLocked = computeIsTableLocked(locked, hasPlaceholderRows);
      const selectedCells = computeSelectedEvaluatedCells({
        hasMultiSelection: selection.hasMultiSelection,
        expandedBox: selection.expandedBox,
        computedTable,
        table,
      });
      const isCellSelectionLocked = computeIsCellSelectionEditingLocked({
        isTableLocked,
        selectedCells,
        rowData: table.rowData,
      });
      const selectionForWarning = selection.hasMultiSelection ? selection.expandedBox : null;
      const editWhileTableLockedWarning = resolveEditWhileTableLockedWarning(editWhileTableLockedWarningProp);
      const getEditWhileCellLockedWarning = resolveGetEditWhileCellLockedWarning(
        getEditWhileCellLockedWarningProp
      );
      const lockedWarningMessage = computeLockedWarningMessage({
        editingIsLocked: isCellSelectionLocked,
        isTableLocked,
        editWhileTableLockedWarning,
        getEditWhileCellLockedWarning,
        selection: selectionForWarning,
      });
      const anchorSelectedCells = computeAnchorSelectedEvaluatedCells({
        hasMultiSelection: selection.hasMultiSelection,
        multiStartIndex,
        computedTable,
        table,
      });
      const isAnchorCellSelectionLocked = computeIsCellSelectionEditingLocked({
        isTableLocked,
        selectedCells: anchorSelectedCells,
        rowData: table.rowData,
      });
      const isCellSelectionLockedForUndoRedo = computeIsCellSelectionEditingLocked({
        isTableLocked,
        selectedCells,
        rowData: table.rowData,
        forUndoRedo: true,
      });
      const editWhileTableLockedWarningMessage = locked
        ? computeLockedWarningMessage({
            editingIsLocked: true,
            isTableLocked,
            editWhileTableLockedWarning,
            getEditWhileCellLockedWarning: null,
            selection: null,
          })
        : null;
      const lockedWarningMessageIfAnchorLocked = computeLockedWarningMessage({
        editingIsLocked: isAnchorCellSelectionLocked,
        isTableLocked,
        editWhileTableLockedWarning,
        getEditWhileCellLockedWarning,
        selection: selectionForWarning,
      });
      const lockedWarningMessageForUndoRedo = computeLockedWarningMessage({
        editingIsLocked: isCellSelectionLockedForUndoRedo,
        isTableLocked,
        editWhileTableLockedWarning,
        getEditWhileCellLockedWarning,
        selection: selectionForWarning,
      });
      return {
        isTableLocked,
        isCellSelectionLocked,
        isLockedWithEditWarn: isCellSelectionLocked,
        lockedWarningMessage,
        isAnchorCellSelectionLocked,
        isCellSelectionLockedForUndoRedo,
        editWhileTableLockedWarningMessage,
        lockedWarningMessageIfAnchorLocked,
        lockedWarningMessageForUndoRedo,
      };
    }
  );
}
